// Optik-Shop (Design §11.2): accounts spend reward points on cosmetics and pick what they wear,
// each item with an animated preview. Opened from the profile box on the start page.
import { el, openWindow } from './dom.js';
import { type Item, type Profile, shop } from './identity.js';
import { FLAG_BASE } from './colors.js';
import { FLAGS, drawFlag } from './flags.js';
import { BULLET, COLOR, DESTRUCTION, EMOTES, EMOTE_KEYS, Effects, KIND_NAMES, LOOK_SLOTS, MUZZLE, PAINT, POSE, REPLACES_BULLET, SHOP_KINDS, TRACKS } from './looks.js';
import { EMOTES as EMOTE_LIST, drawBubble, ownedEmotes, packEmotes } from './emotes.js';
import { drawPose } from './poses.js';
import { type Constants, drawTank, playerColor } from './render.js';

const PREVIEW_W = 150; // CSS px, the same shape as a pose box
const PREVIEW_H = 90;
const FLAG_W = 66; // flags: many, smaller and drawn once, the flag itself beside the tank
const FLAG_H = 66;
const FLAG_PIC_W = 42;
const FLAG_PIC_H = 28;
const GROUPS = ['Europe', 'Asia', 'Africa', 'Americas', 'Oceania', 'Other'];
const DEATH_CYCLE_MS = 3000; // destruction preview: alive for a moment, then the wreck
const ALIVE_MS = 500;
const SHOT_MS = 700; // muzzle flash preview: one shot this often
const EMOTE_MS = 1300; // emote pack preview: each of its emotes this long

const SHOP_ERRORS: Record<string, string> = {
  unknown: 'That does not exist.',
  owned: 'You already own that.',
  points: 'Not enough points.',
  not_owned: 'Buy it first.',
};

type Preview = (now: number) => void;

/** Animated preview of `item` on `canvas` (`cw` × `ch` CSS px), one tank in the first player colour. */
function preview(canvas: HTMLCanvasElement, item: Item, c: Constants, cw = PREVIEW_W, ch = PREVIEW_H, worn?: number[]): Preview {
  const ctx = canvas.getContext('2d')!;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = cw * dpr;
  canvas.height = ch * dpr;
  if (item.kind === EMOTES) {
    // A tank saying the pack's emotes in turn.
    const scale = (cw * dpr) / (c.tankRadius * 9);
    const [w, h] = [canvas.width / scale, canvas.height / scale];
    const tank = new Float32Array(c.tankStride);
    const ids = packEmotes(item.id);
    return (now) => {
      tank.set([w / 2, h * 0.72, -Math.PI / 2, 1, 1]);
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.fillStyle = '#d9d4c7';
      ctx.fillRect(0, 0, w, h);
      drawTank(ctx, tank, 0, playerColor(0), c, false, undefined, 0, true, undefined, now);
      const k = Math.floor(now / EMOTE_MS) % ids.length;
      drawBubble(ctx, w / 2, h * 0.72 + 6, c.tankRadius, ids[k], (now % EMOTE_MS) / EMOTE_MS * 0.9, now);
    };
  }
  if (item.kind === POSE) {
    return (now) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = '#d9d4c7';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawPose(ctx, item.id, canvas.width, playerColor(0), c, 0, undefined, now);
    };
  }
  const close = item.kind === PAINT || item.kind === COLOR;
  const scale = (cw * dpr) / (c.tankRadius * (close ? 4.5 : item.kind === MUZZLE ? 6 : 9)); // close up
  const [w, h] = [canvas.width / scale, canvas.height / scale];
  const looks = new Uint16Array(LOOK_SLOTS);
  looks[item.kind] = item.id;
  // A paint shows over the worn colour look, a colour look under the worn paint: as in the game.
  if (item.kind === PAINT) looks[COLOR] = worn?.[COLOR] ?? 0;
  if (item.kind === COLOR) looks[PAINT] = worn?.[PAINT] ?? 0;
  const effects = new Effects(true); // previews make no sound
  const tank = new Float32Array(c.tankStride);
  const bullets = new Float32Array(item.kind === BULLET || item.kind === MUZZLE ? c.bulletStride : 0);
  const color = playerColor(0);

  return (now) => {
    tank.set([w / 2, h / 2, 0, 1, 1]);
    if (close) tank[2] = now / 1500;
    if (item.kind === BULLET) {
      // The tank faces a bullet circling in front of it.
      tank.set([c.tankRadius * 1.6, h / 2, 0]);
      const a = now / 350;
      bullets.set([1, w * 0.62 + Math.cos(a) * h * 0.3, h / 2 + Math.sin(a) * h * 0.3, 0, 0]);
    }
    if (item.kind === TRACKS) {
      // Drives round an oval, leaving its prints.
      const a = now / 400;
      const [rx, ry] = [w * 0.28, h * 0.28];
      tank.set([w / 2 + Math.cos(a) * rx, h / 2 + Math.sin(a) * ry, Math.atan2(Math.cos(a) * ry, -Math.sin(a) * rx)]);
    }
    if (item.kind === MUZZLE) {
      // Fires to the right again and again; each shot is a new bullet.
      tank.set([c.tankRadius * 1.2, h / 2, 0]);
      const t = now % SHOT_MS;
      bullets.set([1 + Math.floor(now / SHOT_MS), tank[0] + c.barrelLength + t * 0.12, h / 2, 0, 0]);
    }
    if (item.kind === DESTRUCTION) {
      // Low in the frame: several effects rise from the wreck.
      tank.set([w / 2, h * 0.68, 0, 1, now % DEATH_CYCLE_MS < ALIVE_MS ? 1 : 0]);
    }
    effects.update(tank, c.tankStride, bullets, c.bulletStride, looks, playerColor, now);
    effects.layTracks(tank, c.tankStride, looks, () => 1, now);
    effects.shots(tank, c.tankStride, bullets, c.bulletStride, undefined, c, () => true, now);

    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = '#d9d4c7';
    ctx.fillRect(0, 0, w, h);
    effects.drawTracks(ctx, c.tankRadius, looks, playerColor, now);
    drawTank(ctx, tank, 0, color, c, false, undefined, looks[PAINT], effects.hasTurret(0), undefined, now, true, 0, looks[COLOR]);
    effects.drawFlashes(ctx, c.tankRadius, looks, playerColor, now);
    effects.drawTrails(ctx, bullets, c.bulletStride, c.bulletRadius, looks, playerColor, now);
    if (bullets.length && !REPLACES_BULLET.has(item.id)) {
      ctx.beginPath();
      ctx.arc(bullets[1], bullets[2], c.bulletRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#111';
      ctx.fill();
    }
    effects.drawBursts(ctx, c, now);
  };
}

/** The flag itself, small, framed. */
function flagPicture(flag: (typeof FLAGS)[number]): HTMLCanvasElement {
  const canvas = el('canvas', { className: 'flag' });
  const dpr = window.devicePixelRatio || 1;
  canvas.width = FLAG_PIC_W * dpr;
  canvas.height = FLAG_PIC_H * dpr;
  canvas.style.width = `${FLAG_PIC_W}px`;
  canvas.style.height = `${FLAG_PIC_H}px`;
  drawFlag(canvas.getContext('2d')!, flag, canvas.width, canvas.height);
  return canvas;
}

/** Opens the shop; `closed` runs when it closes (points and looks may have changed). */
export function openWardrobe(start: Profile, c: Constants, closed: () => void): void {
  const panel = el('div', { id: 'wardrobe', className: 'panel' });
  const close = openWindow(panel, closed);
  let previews: Preview[] = [];
  let tab = COLOR; // shown kind, kept across buying and equipping
  let query = ''; // flag search

  function render(me: Profile, error = ''): void {
    previews = [];
    const tabs = SHOP_KINDS.map((kind) =>
      el('button', {
        className: kind === tab ? 'tab active' : 'tab',
        textContent: KIND_NAMES[kind],
        onclick: () => {
          tab = kind;
          render(me, error);
        },
      }),
    );
    const standard = { id: 0, kind: tab, name: 'Standard', price: 0 };
    // Emote packs are bought, not worn: no standard card; the keys above them.
    const items = [...(tab === EMOTES ? [] : [standard]), ...me.catalog.filter((i) => i.kind === tab && (tab !== COLOR || i.id < FLAG_BASE))];
    const flags = tab === COLOR ? flagList(me) : '';
    const keys = tab === EMOTES ? emoteKeys(me) : '';
    panel.replaceChildren(
      el(
        'div',
        { className: 'head' },
        el('h2', { textContent: 'Cosmetics' }),
        el('span', { className: 'points', textContent: `${me.points} points` }),
        el('button', { textContent: 'Close', onclick: close }),
      ),
      el('p', { className: 'hint', textContent: 'Applies from the next time you join a lobby. Cosmetic only – no advantage in the game.' }),
      error ? el('p', { className: 'error', textContent: error }) : '',
      el('div', { className: 'tabs' }, ...tabs),
      keys,
      el('div', { className: 'items' }, ...items.map((item) => card(me, item))),
      flags,
    );
  }

  /** The flags under the patterns: a search field, then the flags by continent, A to Z. */
  function flagList(me: Profile): HTMLElement {
    const byId = new Map(me.catalog.map((i) => [i.id, i]));
    const list = el('div', { className: 'flags' });
    const fill = () => {
      const q = query.trim().toLowerCase();
      // Without a search only the flags already bought: all of them would bury the patterns.
      list.replaceChildren(
        ...GROUPS.flatMap((group) => {
          const shown = FLAGS.map((f, k) => ({ f, item: byId.get(FLAG_BASE + k) }))
            .filter(({ f, item }) => item && f.group === group && (q ? f.name.toLowerCase().includes(q) : me.owned.includes(item.id)))
            .sort((a, b) => a.f.name.localeCompare(b.f.name, 'en'));
          return shown.length ? [el('h3', { textContent: group }), el('div', { className: 'items' }, ...shown.map(({ item }) => card(me, item!, true)))] : [];
        }),
      );
      if (!list.childElementCount) list.append(el('p', { className: 'hint', textContent: q ? 'No flag found.' : 'Type a country to see its flag.' }));
    };
    const search = el('input', { type: 'search', placeholder: 'Search flags …', value: query });
    search.oninput = () => {
      query = search.value;
      fill();
    };
    fill();
    return el('div', {}, el('h3', { textContent: `Flags (${FLAGS.length})` }), search, list);
  }

  /** The emotes on the keys 1–4: each picked from the free ones and those of the packs bought. */
  function emoteKeys(me: Profile): HTMLElement {
    const mine = ownedEmotes(me.owned);
    const slots = (me.emotes ?? [0, 1, 2, 3]).map((current, k) => {
      const select = el('select', {}, ...mine.map((id) => el('option', { value: String(id), textContent: EMOTE_LIST[id].label, selected: id === current })));
      select.onchange = () => act('equip', { kind: EMOTE_KEYS + k, item: Number(select.value) });
      return el('label', { className: 'key' }, el('b', { textContent: String(k + 1) }), select);
    });
    return el(
      'div',
      { className: 'emote-keys' },
      el('h3', { textContent: 'Your keys' }),
      el('p', { className: 'hint', textContent: 'Keys 1–4 in a lobby (controller: Y picks, X sends; touch: 💬). GG, Oops, Hi and Thanks are free; packs add four each.' }),
      el('div', { className: 'keys' }, ...slots),
    );
  }

  /** One item; `still`: a flag, drawn once and smaller (there are many). */
  function card(me: Profile, item: Item, still = false): HTMLElement {
    const worn = me.looks[item.kind] === item.id;
    const owned = item.id === 0 || me.owned.includes(item.id);
    const canvas = el('canvas');
    const [cw, ch] = still ? [FLAG_W, FLAG_H] : [PREVIEW_W, PREVIEW_H];
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    const draw = preview(canvas, item, c, cw, ch, me.looks);
    if (still) draw(0);
    else previews.push(draw);
    const picture = still ? el('div', { className: 'pictures' }, flagPicture(FLAGS[item.id - FLAG_BASE]), canvas) : canvas;
    const button = worn
      ? el('button', { textContent: 'Equipped', disabled: true })
      : owned && item.kind === EMOTES
        ? el('button', { textContent: 'Owned', disabled: true })
        : owned
        ? el('button', { textContent: 'Equip', onclick: () => act('equip', { kind: item.kind, item: item.id }) })
        : el('button', {
            className: 'primary',
            textContent: `Buy – ${item.price} P`,
            disabled: me.points < item.price,
            onclick: () => act('buy', { item: item.id }),
          });
    return el(
      'div',
      { className: worn ? 'item worn' : 'item' },
      picture,
      el('div', { className: 'name', textContent: item.name }),
      button,
    );
  }

  async function act(action: 'buy' | 'equip', body: object): Promise<void> {
    let result: Profile | string;
    try {
      result = await shop(action, body);
    } catch {
      result = 'offline';
    }
    if (result === 'session') return close();
    if (typeof result === 'string') return render(start, SHOP_ERRORS[result] ?? 'Server unreachable.');
    start = result;
    render(result);
  }

  function frame(now: number): void {
    if (!panel.isConnected) return;
    for (const p of previews) p(now);
    requestAnimationFrame(frame);
  }

  render(start);
  requestAnimationFrame(frame);
}
